/** Mobilierul unui plan de arhitectură, la scara lui adevărată.
 *
 *  DE CE E AICI ȘI NU DECOR. Un living de 18 m² în care nu încap o canapea și o
 *  masă, sau un dormitor de 12 m² în care patul nu lasă pe unde să treci, e o
 *  cameră care a trecut la ARIE și pică la FOLOSINȚĂ. Norma dă metri pătrați;
 *  desenul arată dacă acei metri pătrați se pot mobila. De asta fiecare piesă de
 *  aici are dimensiunea ei reală, în metri — pat dublu 1,60 × 2,00, canapea
 *  2,20 × 0,90, vas 0,37 × 0,60, mașină 1,80 × 4,50 — și de asta o piesă care nu
 *  încape NU se desenează micșorată, ci deloc. O canapea strivită la 1,1 m ar
 *  spune că livingul e bun, și ar minți.
 *
 *  CADRUL. Fiecare piesă se desenează în cadrul PROPRIU al camerei: originea în
 *  centrul ei, `x` pe latura lungă, `y` pe cea scurtă. O bandă de apartamente
 *  cotește, deci jumătate din camerele unui etaj stau la 90° față de cealaltă
 *  jumătate; măsurate pe axele lumii, paturile ar ieși pe latura greșită la
 *  fiecare a doua cameră. `cadru()` de mai jos e locul unde se închide asta.
 *
 *  AȘEZAREA e o convenție, nu o deducție. Documentul nu spune pe ce perete e
 *  ușa camerei, deci nu se poate ști „capul patului lângă ușă". Convenția e cea
 *  a planșelor de referință: piesa mare stă lipită de o latură, cu spatele la
 *  ea, iar restul se organizează în jurul ei. E consecventă și se citește; nu
 *  pretinde să fie mobilarea finală.
 */
import { memo } from "react";
import type { PickAttrs } from "../inspect";
import { piesaPlan, MOBILATE_PLAN } from "./arhMobilierPlan";

// Familia de tonuri e a planșelor randate: piese deschise, cu linie caldă, nu
// gri de CAD. Umbra le vine din `arh-umbra-mobila` (v. `arhMaterial`).
// Tonurile sunt ale planșei Cheshme, și regula lor e una singură: PIESA E MAI
// ÎNCHISĂ DECÂT PARDOSEALA. Cu mobilier alb pe pardoseală crem — cum era —
// piesele se pierdeau în fond și planul arăta gol la orice depărtare. Contrastul
// nu e decor: el e cel care spune, dintr-o privire, cât din cameră e ocupat.
// TONURILE TRC PRIN VARIABILE CSS, ca planșa tehnică să le poată stinge.
//
// Piesele se desenează la fel pe amândouă convențiile — aceleași forme, aceleași
// dimensiuni — dar planul TEHNIC e desen de linie: acolo nicio piesă n-are ton,
// doar contur. Variabila lasă comutarea să se facă dintr-un singur `<g>` al
// părintelui, fără să treacă o paletă prin cele douăzeci de funcții de desen și
// prin ajutoarele `r`/`l`, care nu sunt componente și n-ar putea citi un context.
//
// Valoarea de rezervă e exact tonul de dinainte, deci desenul randat rămâne
// neschimbat dacă nimeni nu setează nimic.
const LINIE = "var(--mob-linie, #8a7660)";   // conturul piesei
const CORP = "var(--mob-corp, #f3ece0)";     // corpuri tari deschise: dulap, blat
const LEMN = "var(--mob-lemn, #b4936d)";     // lemn: masă, comodă, blat
const MOALE = "var(--mob-moale, #cdbaa1)";   // corpuri moi: canapea, pat, fotoliu
const COVOR = "var(--mob-covor, #ded0ba)";   // covorul, fără contur
const APA = "var(--mob-apa, #eae3d8)";       // obiecte sanitare
const VERDE = "var(--mob-verde, #a9bd9c)";   // jardiniere

/** Sub cât nu se mai desenează mobilierul, în pixeli pe LATURA SCURTĂ a camerei.
 *
 *  A FOST 22, și era pragul potrivit pentru ALT desen. Atunci mobilierul era
 *  alb cu contur subțire: la 20 px pe cameră, o canapea de 14 px era o zgârietură
 *  și patruzeci de apartamente de zgârieturi arătau ca murdărie pe planșă.
 *
 *  Între timp piesele au devenit corpuri PLINE, mai închise decât pardoseala
 *  (v. `LINIE`/`MOALE`/`LEMN`), iar o pată de ton se citește mult mai mic decât
 *  un contur: la 12 px pe cameră nu mai distingi perna de saltea, dar vezi
 *  limpede că în cameră stă un pat. Măsurat pe un etaj real: la 18 px/m —
 *  încadrarea pe un singur corp, adică vederea cu care se deschide ecranul —
 *  pragul vechi lăsa mobilate 35 de camere din 44, iar la încadrarea pe ansamblu
 *  doar 6 din 44. Adică exact la scările la care se uită omul, planul apărea
 *  gol, și de asta „nu se vedea mobilierul".
 *
 *  10 px e limita sub care pata nu mai are formă deloc. */
export const PRAG_MOBILIER = 10;

/** Cât de mult trebuie să încapă o piesă ca s-o desenăm: latura ei plus un
 *  spațiu de trecere. Sub asta piesa se sare — nu se micșorează. */
const JOC = 0.15;

/** GROSIMEA LINIEI DE MOBILIER, în pixeli, nu în metri.
 *
 *  Toate grosimile de aici erau scrise în METRI de desen, deci se îngroșau
 *  odată cu mărirea — în timp ce pereții, cotele și hașura sunt de mult pe
 *  `non-scaling-stroke`, adică rămân constante. La mărire mare mobilierul ieșea
 *  astfel mai gras decât zidul care îl conține: măsurat pe un living real, la
 *  183 px/m, conturul unei canapele ajungea la 4,6 px față de 1,5 ai peretelui
 *  tăiat. De aceea piesele arătau ca niște pete groase, nu ca desenul din
 *  planșa de referință, unde mobilierul e vizibil mai ușor decât zidăria.
 *
 *  Numerele de la locul fiecărei piese rămân neatinse — ele poartă IERARHIA
 *  (corpul e mai gros decât sertarul) — și se traduc toate prin factorul ăsta.
 *  Podeaua de 0,6 px ține detaliile fine să nu dispară cu totul. */
const K_TUS = 55;
const tus = (m: number) => Math.max(0.6, m * K_TUS);

const r = (x: number, y: number, w: number, h: number,
           fill = CORP, rx = 0.04, sw = 0.025) => (
  <rect x={x} y={y} width={w} height={h} rx={rx}
        fill={fill} stroke={LINIE} strokeWidth={tus(sw)}
        vectorEffect="non-scaling-stroke" />
);

const linie = (x1: number, y1: number, x2: number, y2: number, sw = 0.02) => (
  <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={LINIE} strokeWidth={tus(sw)}
        vectorEffect="non-scaling-stroke" />
);

/** Punctul e în inel? Raza spre stânga, numărul de treceri. */
function inInel(inel: number[][], x: number, y: number): boolean {
  let inz = false;
  for (let k = 0, l = inel.length - 1; k < inel.length; l = k++) {
    const [xk, yk] = inel[k], [xl, yl] = inel[l];
    if ((yk > y) !== (yl > y)
        && x < ((xl - xk) * (y - yk)) / (yl - yk) + xk) inz = !inz;
  }
  return inz;
}

/** Cel mai mare dreptunghi cu laturile pe axele cadrului care încape ÎN inel.
 *
 *  DE CE NU E DE-AJUNS CUTIA. Cadrul era cutia din jurul inelului, iar pe o
 *  cameră dreptunghiulară cele două coincid — ceea ce e cazul a nouă camere din
 *  zece. A zecea nu: un living care ocolește un nucleu, o bucătărie tăiată de
 *  ghenă, o cameră cu un colț mâncat de casa scării. Acolo cutia cuprinde și
 *  scobitura, iar o piesă așezată „lipită de latura din dreapta" cade în
 *  scobitură, adică pe zid. Decupajul pe cameră (v. `MobilierBrut`) o taie, și
 *  se vedea exact așa: un fotoliu din care rămâne jumătate, lipit de perete.
 *
 *  Piesa aia nu trebuia tăiată, trebuia să nu fie desenată acolo — regula casei
 *  e că o piesă care nu încape nu se desenează. Ca s-o poată aplica, piesele au
 *  nevoie de un cadru în care tot ce încape e și înăuntru. Ăsta e.
 *
 *  CUM. Cutia se împarte în celule de ~10 cm, fiecare celulă se întreabă dacă
 *  centrul ei e în inel, și pe harta asta se caută dreptunghiul plin cel mai
 *  mare — histograma clasică, o singură trecere pe rânduri. Rezultatul se
 *  strânge cu o celulă pe fiecare latură, fiindcă o muchie piezișă poate tăia
 *  un colț de celulă al cărei centru era înăuntru; 10 cm pierduți nu mută
 *  nicio piesă, o piesă vărsată în camera vecină da.
 *
 *  Se face DOAR când inelul chiar diferă de cutia lui. Pe un dreptunghi ar fi
 *  muncă degeaba, și — mai rău — l-ar strânge cu cele două celule de siguranță
 *  de care nu are nevoie.
 *
 *  CE ÎNTREABĂ PRAGUL. Nu raportul ariilor singur: livingul care a scos asta la
 *  iveală are zece vârfuri, iar aria lui e la un procent de a cutiei — trece
 *  orice prag de arie — dar e un patrulater ușor strâmb, așa că TOATE cele
 *  patru colțuri ale cutiei cad pe zid, și fix în colț stă fotoliul. Deci se
 *  întreabă amândouă: colțurile, care prind strâmbătura, și aria, care prinde
 *  scobitura din mijlocul unei laturi. Colțurile se pipăie cu doi centimetri
 *  spre interior — pe un dreptunghi ele coincid cu vârfurile inelului, iar un
 *  punct chiar pe contur nu e nici înăuntru nici afară. */
function dreptunghiInterior(uv: number[][], u0: number, v0: number,
                            u1: number, v1: number): [number, number, number, number] {
  const cutie: [number, number, number, number] = [u0, v0, u1, v1];
  const W = u1 - u0, H = v1 - v0;
  if (!(W > 0) || !(H > 0)) return cutie;

  let arie = 0;
  for (let i = 0; i < uv.length; i++) {
    const b = uv[(i + 1) % uv.length];
    arie += uv[i][0] * b[1] - b[0] * uv[i][1];
  }
  const e = 0.02;
  const colturi = [[u0 + e, v0 + e], [u1 - e, v0 + e],
                   [u1 - e, v1 - e], [u0 + e, v1 - e]];
  if (Math.abs(arie) / 2 >= 0.98 * W * H
      && colturi.every(([x, y]) => inInel(uv, x, y))) return cutie;

  const pas = 0.1;
  const nu = Math.max(3, Math.min(160, Math.round(W / pas)));
  const nv = Math.max(3, Math.min(160, Math.round(H / pas)));
  const du = W / nu, dv = H / nv;

  const inauntru = new Uint8Array(nu * nv);
  for (let j = 0; j < nv; j++) {
    const y = v0 + (j + 0.5) * dv;
    for (let i = 0; i < nu; i++) {
      if (inInel(uv, u0 + (i + 0.5) * du, y)) inauntru[j * nu + i] = 1;
    }
  }

  // Dreptunghiul plin maxim: pe fiecare rând, înălțimea coloanei de „pline" de
  // deasupra, apoi cel mai mare dreptunghi din histograma aia.
  const inalt = new Int32Array(nu);
  const stiva = new Int32Array(nu + 1);
  let bestA = 0, best: [number, number, number, number] | null = null;
  for (let j = 0; j < nv; j++) {
    for (let i = 0; i < nu; i++) inalt[i] = inauntru[j * nu + i] ? inalt[i] + 1 : 0;
    let vf = 0;
    for (let i = 0; i <= nu; i++) {
      const h = i < nu ? inalt[i] : 0;
      while (vf > 0 && inalt[stiva[vf - 1]] >= h) {
        const hh = inalt[stiva[--vf]];
        const st = vf > 0 ? stiva[vf - 1] + 1 : 0;
        const a = hh * (i - st);
        if (a > bestA) { bestA = a; best = [st, j + 1 - hh, i, j + 1]; }
      }
      stiva[vf++] = i;
    }
  }
  if (!best) return cutie;

  // O celulă de siguranță pe fiecare latură — v. nota de sus.
  const [ci, cj, ci2, cj2] = best;
  if (ci2 - ci < 3 || cj2 - cj < 3) return cutie;
  return [u0 + (ci + 1) * du, v0 + (cj + 1) * dv,
          u0 + (ci2 - 1) * du, v0 + (cj2 - 1) * dv];
}

/** Cadrul propriu al unei camere: centrul, laturile și unghiul.
 *
 *  Când documentul dă `deg` (camerele generate îl dau), se folosește el. Când
 *  nu — o cameră importată are doar inelul — unghiul se ia de pe CEA MAI LUNGĂ
 *  MUCHIE a inelului. Nu e dreptunghiul de arie minimă, și nu trebuie să fie:
 *  camerele de locuit sunt dreptunghiuri sau L-uri cu laturi paralele, iar pe
 *  ele latura lungă dă exact axa pe care stă mobila.
 *
 *  `w` e mereu latura LUNGĂ, `h` cea scurtă, ca piesele să nu trebuiască să
 *  întrebe care e care.
 *
 *  CADRUL E CEL DIN INTERIORUL CAMEREI, nu cutia din jurul ei. Vezi
 *  `dreptunghiInterior`: pe o cameră care nu e dreptunghi, cele două nu sunt
 *  același lucru, iar piesele așezate în a doua ies din cameră.
 */
export function cadru(poly: number[][], deg?: number | null):
    { cx: number; cy: number; w: number; h: number; deg: number } | null {
  if (!poly || poly.length < 3) return null;
  let a: number;
  if (deg != null && Number.isFinite(deg)) {
    a = (deg * Math.PI) / 180;
  } else {
    let best = -1; a = 0;
    for (let i = 0; i < poly.length; i++) {
      const [x0, y0] = poly[i];
      const [x1, y1] = poly[(i + 1) % poly.length];
      const d = Math.hypot(x1 - x0, y1 - y0);
      if (d > best) { best = d; a = Math.atan2(y1 - y0, x1 - x0); }
    }
  }
  const c = Math.cos(a), s = Math.sin(a);
  let u0 = Infinity, u1 = -Infinity, v0 = Infinity, v1 = -Infinity;
  for (const [x, y] of poly) {
    const u = x * c + y * s, v = -x * s + y * c;
    if (u < u0) u0 = u; if (u > u1) u1 = u;
    if (v < v0) v0 = v; if (v > v1) v1 = v;
  }
  // Cutia, strânsă la ce e chiar înăuntru — v. `dreptunghiInterior`.
  const uv = poly.map(([x, y]) => [x * c + y * s, -x * s + y * c]);
  const [i0, j0, i1, j1] = dreptunghiInterior(uv, u0, v0, u1, v1);
  const cu = (i0 + i1) / 2, cv = (j0 + j1) / 2;
  let w = i1 - i0, h = j1 - j0, ang = a;
  if (!(w > 0) || !(h > 0)) return null;
  // Latura lungă pe `x`: dacă inelul iese mai înalt decât lat, cadrul se
  // rotește cu 90°, nu se schimbă doar eticheta — altfel `x` ar rămâne pe
  // latura scurtă și toate piesele ar sta de-a curmezișul.
  if (h > w) { [w, h] = [h, w]; ang = a + Math.PI / 2; }
  return { cx: cu * c - cv * s, cy: cu * s + cv * c, w, h, deg: (ang * 180) / Math.PI };
}

// ---------------------------------------------------------------------------
// Piesele
// ---------------------------------------------------------------------------

/** Pat: saltea, pernă (sau două), cuta pilotei, plus noptiere și dulap dacă
 *  mai e loc. Capul patului stă pe latura de sus a cadrului. */
function Pat({ w, h }: { w: number; h: number }) {
  const dublu = w >= 2.9 || h >= 2.9 ? true : Math.min(w, h) >= 2.6;
  const pw = dublu ? 1.6 : 0.9;          // lățimea patului
  const ph = 2.0;                        // lungimea lui
  if (h < ph + JOC || w < pw + JOC) return null;
  const x = -w / 2 + 0.12, y = -h / 2 + 0.1;
  return (
    <g>
      {/* Covorul PRIMUL, fiindcă e dedesubt. Ordinea nu e cosmetică: desenat
          după pat, l-ar acoperi — și exact asta s-a și întâmplat la prima
          încercare, împreună cu o a doua saltea peste perne. */}
      {w >= pw + 0.9 && h >= ph + 0.6 && (
        <rect x={x - 0.22} y={y + 0.5} width={pw + 0.44} height={ph}
              rx={0.03} fill={COVOR} />
      )}
      {r(x, y, pw, ph, MOALE, 0.05)}
      {/* pernele, la cap */}
      {dublu ? (
        <>
          {r(x + 0.08, y + 0.08, pw / 2 - 0.12, 0.34, CORP, 0.06, 0.018)}
          {r(x + pw / 2 + 0.04, y + 0.08, pw / 2 - 0.12, 0.34, CORP, 0.06, 0.018)}
        </>
      ) : r(x + 0.08, y + 0.08, pw - 0.16, 0.34, CORP, 0.06, 0.018)}
      {/* cuta pilotei: linia care face patul să se citească ca pat */}
      {linie(x, y + 0.62, x + pw, y + 0.62)}
      {/* noptiera, doar dacă rămâne culoar de trecere lângă pat */}
      {w >= pw + 0.5 + JOC && r(x + pw + 0.06, y, 0.42, 0.4)}
      {/* a doua noptieră, pe partea cealaltă, când patul e dublu și încape */}
      {dublu && x - 0.48 > -w / 2 && r(x - 0.48, y, 0.42, 0.4)}
      {/* dulapul pe peretele opus, cu ușile marcate */}
      {w >= pw + 0.6 + 0.65 && (
        <>
          {r(w / 2 - 0.72, -h / 2 + 0.1, 0.6, Math.min(2.2, h - 0.2))}
          {linie(w / 2 - 0.72, -h / 2 + 0.1 + Math.min(2.2, h - 0.2) / 2,
                 w / 2 - 0.12, -h / 2 + 0.1 + Math.min(2.2, h - 0.2) / 2, 0.015)}
        </>
      )}
    </g>
  );
}

/** Zona de zi: covor, canapea cu spatele la peretele de jos, măsuță, comodă TV
 *  în față. Când camera e mare (un „Living + bucătărie"), primește și masa de
 *  luat masa, iar bucătăria vine peste ea din `Bucatarie`. */
function Living({ w, h }: { w: number; h: number }) {
  const cw = Math.min(2.2, w - 0.5);
  if (cw < 1.4 || h < 2.4) return null;
  const yJos = h / 2 - 0.1;
  return (
    <g>
      {/* covorul, sub tot — fără contur, e textil */}
      {w > 3 && h > 3 && (
        <rect x={-cw / 2 - 0.2} y={yJos - 2.5} width={cw + 0.4} height={1.9}
              rx={0.03} fill={COVOR} />
      )}
      {/* canapeaua: corp, spătar, trei perne de ședere */}
      {r(-cw / 2, yJos - 0.9, cw, 0.9, MOALE, 0.07)}
      {r(-cw / 2, yJos - 0.9, cw, 0.24, MOALE, 0.06, 0.018)}
      {[1, 2].map((i) => (
        <line key={i} x1={-cw / 2 + (cw * i) / 3} y1={yJos - 0.66}
              x2={-cw / 2 + (cw * i) / 3} y2={yJos} stroke={LINIE} strokeWidth={tus(0.015)} vectorEffect="non-scaling-stroke" />
      ))}
      {/* măsuța */}
      {h > 3 && r(-cw / 4, yJos - 1.85, cw / 2, 0.6, LEMN, 0.05)}
      {/* comoda TV, pe peretele din față */}
      {h > 3.4 && r(-cw / 3, -h / 2 + 0.12, (cw * 2) / 3, 0.35, LEMN)}
      {/* fotoliul, în capătul liber al covorului — planșele de referință n-au
          niciodată doar canapeaua: zona de stat se citește ca zonă abia când
          are două locuri care se privesc. */}
      {w > 3.6 && h > 3 && r(cw / 2 + 0.12, yJos - 1.75, 0.75, 0.75, MOALE, 0.1)}
      {/* planta, colț — mărunt, dar e ce face camera să pară locuită */}
      {w > 3.2 && h > 3.4 && (
        <circle cx={-cw / 2 - 0.05} cy={-h / 2 + 0.45} r={0.26}
                fill={VERDE} stroke={LINIE} strokeWidth={tus(0.02)} vectorEffect="non-scaling-stroke" />
      )}
    </g>
  );
}

/** Masa de luat masa, cu scaunele ei. Se cheamă separat, fiindcă apare și în
 *  living, și în bucătăria mare. `n` scaune, împărțite pe laturile lungi. */
function Masa({ x, y, lung, lat }: { x: number; y: number; lung: number; lat: number }) {
  const n = lung >= 1.7 ? 3 : 2;
  const sc = 0.42;
  return (
    <g>
      {r(x - lung / 2, y - lat / 2, lung, lat, LEMN, 0.05)}
      {Array.from({ length: n }, (_, i) => {
        const cx = x - lung / 2 + (lung * (i + 0.5)) / n;
        return (
          <g key={i}>
            {r(cx - sc / 2, y - lat / 2 - sc - 0.06, sc, sc, CORP, 0.08, 0.018)}
            {r(cx - sc / 2, y + lat / 2 + 0.06, sc, sc, CORP, 0.08, 0.018)}
          </g>
        );
      })}
    </g>
  );
}

/** Bucătăria: blat de 0,60 m pe o latură, cu chiuvetă și patru ochiuri, plus
 *  frigider la capăt și insulă dacă încăperea o poate primi. */
function Bucatarie({ w, h }: { w: number; h: number }) {
  const bl = Math.min(w - 0.3, 3.4);
  if (bl < 1.2 || h < 1.8) return null;
  const y = -h / 2 + 0.1;
  const x0 = -bl / 2;
  return (
    <g>
      {r(x0, y, bl, 0.6, LEMN, 0.03)}
      {/* chiuveta: cuvă plus baterie */}
      {r(x0 + 0.14, y + 0.1, 0.5, 0.4, APA, 0.05, 0.018)}
      <circle cx={x0 + 0.39} cy={y + 0.07} r={0.045} fill="none"
              stroke={LINIE} strokeWidth={tus(0.018)} vectorEffect="non-scaling-stroke" />
      {/* plita: patru ochiuri */}
      {[[0.24, 0.2], [0.52, 0.2], [0.24, 0.44], [0.52, 0.44]].map(([dx, dy], i) => (
        <circle key={i} cx={x0 + bl - 0.75 + dx} cy={y + dy} r={0.075}
                fill="none" stroke={LINIE} strokeWidth={tus(0.02)} vectorEffect="non-scaling-stroke" />
      ))}
      {/* frigiderul, la capătul blatului */}
      {bl > 2.2 && (
        <>
          {r(x0 + bl - 0.7, y + 0.62, 0.68, 0.68)}
          {linie(x0 + bl - 0.7, y + 0.96, x0 + bl - 0.02, y + 0.96, 0.015)}
        </>
      )}
      {/* insula, doar într-o bucătărie care chiar o poate ocoli */}
      {h >= 3.4 && bl >= 2.4 && r(-0.9, 0.15, 1.8, 0.8, LEMN, 0.04)}
    </g>
  );
}

/** Baie completă: cadă, lavoar, vas. Duș în locul căzii când camera e scurtă —
 *  o cadă de 1,70 într-o baie de 1,60 nu există, iar desenul n-are voie s-o
 *  arate. */
function Baie({ w, h }: { w: number; h: number }) {
  if (w < 1.1 || h < 0.9) return null;
  const sus = -h / 2 + 0.08, jos = h / 2 - 0.08;
  const cada = w >= 1.7 + JOC;
  return (
    <g>
      {cada ? (
        <g>
          {r(-w / 2 + 0.06, sus, 1.7, 0.72, APA, 0.09)}
          <ellipse cx={-w / 2 + 0.06 + 1.7 - 0.28} cy={sus + 0.36} rx={0.05} ry={0.05}
                   fill="none" stroke={LINIE} strokeWidth={tus(0.018)} vectorEffect="non-scaling-stroke" />
        </g>
      ) : (
        <g>
          {r(-w / 2 + 0.06, sus, 0.9, 0.9, APA, 0.03)}
          {linie(-w / 2 + 0.06, sus, -w / 2 + 0.96, sus + 0.9, 0.018)}
        </g>
      )}
      {/* lavoarul */}
      {w > 1.9 && (
        <g>
          {r(w / 2 - 0.66, sus, 0.6, 0.45, APA, 0.07, 0.02)}
          <circle cx={w / 2 - 0.36} cy={sus + 0.09} r={0.04} fill="none"
                  stroke={LINIE} strokeWidth={tus(0.016)} vectorEffect="non-scaling-stroke" />
        </g>
      )}
      {/* vasul: 0,37 × 0,60, lipit de peretele de jos */}
      {h >= 1.5 && <Vas x={w / 2 - 0.34} y={jos - 0.6} />}
      {/* oglinda peste lavoar și suportul de prosoape — piese mici, dar ele
          sunt diferența dintre „o baie" și „un dreptunghi cu o cadă". */}
      {w > 1.9 && r(w / 2 - 0.62, sus - 0.09, 0.52, 0.07, CORP, 0.02, 0.015)}
      {w > 2.2 && r(-w / 2 + 0.1, jos - 0.5, 0.08, 0.5, CORP, 0.02, 0.015)}
    </g>
  );
}

/** Vasul de closet, desenat ca pe planșă: rezervorul dreptunghiular la perete
 *  și cuva ovală în față. */
function Vas({ x, y }: { x: number; y: number }) {
  return (
    <g>
      {r(x - 0.185, y, 0.37, 0.16, CORP, 0.02, 0.018)}
      <ellipse cx={x} cy={y + 0.42} rx={0.17} ry={0.22} fill={APA}
               stroke={LINIE} strokeWidth={tus(0.02)} vectorEffect="non-scaling-stroke" />
    </g>
  );
}

/** Grup sanitar mic: vas plus lavoar îngust. */
function Wc({ w, h }: { w: number; h: number }) {
  if (w < 0.8 || h < 1.1) return null;
  return (
    <g>
      <Vas x={0} y={-h / 2 + 0.12} />
      {h > 1.7 && r(-0.22, h / 2 - 0.45, 0.44, 0.34, APA, 0.06, 0.02)}
    </g>
  );
}

/** Holul: comodă de intrare. Puțin, dinadins — un hol mobilat peste măsură nu
 *  mai arată că e culoar de trecere. */
function Hol({ w, h }: { w: number; h: number }) {
  if (w < 1.4 || h < 1.1) return null;
  return r(-Math.min(1.0, w - 0.6) / 2, -h / 2 + 0.08, Math.min(1.0, w - 0.6), 0.35);
}

/** Cămara/debaraua: rafturi. */
function Camara({ w, h }: { w: number; h: number }) {
  if (w < 0.7 || h < 0.6) return null;
  return (
    <g>
      {linie(-w / 2 + 0.06, -h / 2 + 0.28, w / 2 - 0.06, -h / 2 + 0.28)}
      {h > 1.2 && linie(-w / 2 + 0.06, -h / 2 + 0.58, w / 2 - 0.06, -h / 2 + 0.58)}
    </g>
  );
}

/** Balconul: măsuță cu două scaune și o jardinieră pe parapet. E singura piesă
 *  care spune că balconul e SPAȚIU DE STAT, nu o consolă de beton. */
function Balcon({ w, h }: { w: number; h: number }) {
  if (w < 1.5 || h < 1.0) return null;
  return (
    <g>
      <circle cx={-w / 4} cy={0} r={0.32} fill={CORP} stroke={LINIE} strokeWidth={tus(0.022)} vectorEffect="non-scaling-stroke" />
      {r(-w / 4 - 0.62, -0.2, 0.36, 0.4, CORP, 0.07, 0.018)}
      {r(-w / 4 + 0.26, -0.2, 0.36, 0.4, CORP, 0.07, 0.018)}
      {w > 2.4 && r(w / 2 - 1.3, h / 2 - 0.42, 1.2, 0.34, VERDE, 0.03)}
    </g>
  );
}

/** Casa scării: scara cu DOUĂ RAMPE și podest, semnul de pe orice planșă de
 *  bloc.
 *
 *  Rampele merg pe latura LUNGĂ a casei, una lângă alta, cu ochiul scării între
 *  ele; podestul stă la capătul spre care se urcă. Treptele sunt la 0,30 m —
 *  pasul real al unei trepte de bloc — deci numărul lor nu e ales, e
 *  `lungime / 0,30`, și o casă mai lungă chiar arată mai multe trepte. Linia
 *  de urcare pleacă dintr-un cerc mic (piciorul primei trepte), merge pe
 *  rampa de jos, cotește pe podest și se întoarce pe rampa de sus până la
 *  vârful săgeții: așa se citește sensul fără niciun cuvânt.
 *
 *  Sub 2,4 × 1,8 m nu încap două rampe; rămâne o singură rampă cu săgeata ei,
 *  ca la o scară de casă. */
function Scara({ w, h }: { w: number; h: number }) {
  if (w < 1.6 || h < 1.0) return null;
  const TREAPTA = 0.3;
  const x0 = -w / 2 + 0.1, x1 = w / 2 - 0.1;
  if (w < 2.4 || h < 1.8) {
    const n = Math.max(3, Math.min(20, Math.floor((w - 0.2) / TREAPTA)));
    const lat = h - 0.2;
    return (
      <g>
        {r(x0, -lat / 2, n * TREAPTA, lat, CORP, 0, 0.022)}
        {Array.from({ length: n - 1 }, (_, i) => (
          <line key={i} x1={x0 + (i + 1) * TREAPTA} y1={-lat / 2}
                x2={x0 + (i + 1) * TREAPTA} y2={lat / 2}
                stroke={LINIE} strokeWidth={tus(0.016)} vectorEffect="non-scaling-stroke" />
        ))}
        <circle cx={x0 + 0.25} cy={0} r={0.07} fill="none" stroke={LINIE} strokeWidth={tus(0.03)} vectorEffect="non-scaling-stroke" />
        <path d={`M${x0 + 0.32} 0 L${x0 + n * TREAPTA - 0.2} 0`} stroke={LINIE}
              strokeWidth={tus(0.03)} vectorEffect="non-scaling-stroke" fill="none" />
        <path d={`M${x0 + n * TREAPTA - 0.2} 0 l-0.16 -0.09 l0 0.18 Z`} fill={LINIE} />
      </g>
    );
  }
  // Podestul: cât o lățime de rampă, dar nu peste 1,4 m.
  const ochi = Math.min(0.2, h * 0.06);
  const lat = (h - 0.2 - ochi) / 2;
  const pod = Math.min(1.4, Math.max(1.0, lat));
  const xp = x1 - pod;                              // începutul podestului
  const n = Math.max(3, Math.min(24, Math.floor((xp - x0) / TREAPTA)));
  const xr = xp - n * TREAPTA;                      // începutul rampelor
  const yA = -ochi / 2 - lat / 2;                   // axa rampei de sus
  const yB = ochi / 2 + lat / 2;                    // axa rampei de jos
  const trepte = (y: number) => Array.from({ length: n - 1 }, (_, i) => (
    <line key={i} x1={xr + (i + 1) * TREAPTA} y1={y - lat / 2}
          x2={xr + (i + 1) * TREAPTA} y2={y + lat / 2}
          stroke={LINIE} strokeWidth={tus(0.016)} vectorEffect="non-scaling-stroke" />
  ));
  return (
    <g>
      {/* rampele și podestul, corpuri deschise pe pardoseala de beton */}
      {r(xr, yA - lat / 2, n * TREAPTA, lat, CORP, 0, 0.022)}
      {r(xr, yB - lat / 2, n * TREAPTA, lat, CORP, 0, 0.022)}
      {r(xp, -h / 2 + 0.1, pod, h - 0.2, CORP, 0, 0.022)}
      {trepte(yA)}
      {trepte(yB)}
      {/* ochiul scării: banda dintre rampe, în tonul liniei */}
      <rect x={xr} y={-ochi / 2} width={n * TREAPTA} height={ochi} fill={LINIE} opacity={0.35} />
      {/* linia de urcare: cerc la plecare, cot pe podest, săgeată la sosire */}
      <circle cx={xr + 0.22} cy={yB} r={0.07} fill="none" stroke={LINIE} strokeWidth={tus(0.03)} vectorEffect="non-scaling-stroke" />
      <path d={`M${xr + 0.3} ${yB} L${xp + pod / 2} ${yB} L${xp + pod / 2} ${yA} L${xr + 0.4} ${yA}`}
            stroke={LINIE} strokeWidth={tus(0.03)} vectorEffect="non-scaling-stroke" fill="none" strokeLinejoin="round" />
      <path d={`M${xr + 0.24} ${yA} l0.18 -0.09 l0 0.18 Z`} fill={LINIE} />
    </g>
  );
}

/** Ghena de instalații: coloanele văzute în secțiune — trei țevi pe axa lungă
 *  — și o diagonală subțire, semnul golului care trece prin planșeu. Fără ea,
 *  o ghenă și o debara sunt același dreptunghi gol. */
function Ghena({ w, h }: { w: number; h: number }) {
  if (w < 0.5 || h < 0.5) return null;
  const a = w - 0.16, b = h - 0.16;
  const n = a >= 1.2 ? 3 : a >= 0.8 ? 2 : 1;
  const pas = a / (n + 1);
  return (
    <g>
      {linie(-a / 2, -b / 2, a / 2, b / 2, 0.014)}
      {Array.from({ length: n }, (_, i) => (
        <circle key={i} cx={-a / 2 + pas * (i + 1)} cy={0} r={Math.min(0.11, b / 5)}
                fill={CORP} stroke={LINIE} strokeWidth={tus(0.022)} vectorEffect="non-scaling-stroke" />
      ))}
    </g>
  );
}

/** Ascensorul: cabina cu cele două diagonale — semnul golului de puț — și
 *  pragul ușii, îngroșat, pe latura scurtă dinspre `+y` (holul de nivel stă
 *  acolo pe nucleele generate). Cabina umple puțul mai puțin cu 0,16 m pe
 *  fiecare latură: ce rămâne e glisiera și contragreutatea. */
function Lift({ w, h }: { w: number; h: number }) {
  if (w < 0.9 || h < 0.9) return null;
  const a = w - 0.32, b = h - 0.32;
  return (
    <g>
      {r(-a / 2, -b / 2, a, b, CORP, 0, 0.025)}
      {linie(-a / 2, -b / 2, a / 2, b / 2, 0.016)}
      {linie(-a / 2, b / 2, a / 2, -b / 2, 0.016)}
      {linie(-a / 2 + 0.12, b / 2, a / 2 - 0.12, b / 2, 0.05)}
    </g>
  );
}

/** Piesele fiecărei funcțiuni. Cheia e `kind`-ul spațiului — aceleași valori
 *  ca în `arhMaterial.PARDOSEALA`, ca cele două tabele să se poată citi
 *  alături. */
function piesa(kind: string, w: number, h: number) {
  switch (kind) {
    case "living": {
      // Un „Living + bucătărie" e tot `living` în document, dar are 40 m². Sub
      // 20 m² e o cameră de zi și primește doar zona de stat; peste, primește
      // și masa — fiindcă acolo chiar se mănâncă.
      const mare = w * h >= 20;
      return (
        <g>
          <Living w={w} h={h} />
          {mare && <Masa x={Math.min(w / 2 - 1.3, w / 4)} y={-h / 2 + 1.05}
                          lung={1.5} lat={0.9} />}
        </g>
      );
    }
    case "bedroom": case "dormitor": return <Pat w={w} h={h} />;
    case "kitchen": return <Bucatarie w={w} h={h} />;
    case "bath": case "bath2": return <Baie w={w} h={h} />;
    case "wc": return <Wc w={w} h={h} />;
    case "shower": return <Baie w={w} h={Math.min(h, 1.4)} />;
    case "hall": case "hol": case "sas": return <Hol w={w} h={h} />;
    case "store": return <Camara w={w} h={h} />;
    case "ghena": return <Ghena w={w} h={h} />;
    case "balcony": return <Balcon w={w} h={h} />;
    case "scara": return <Scara w={w} h={h} />;
    case "lift": return <Lift w={w} h={h} />;
    default: return null;
  }
}

/** Kind-urile care primesc mobilier. Ține locul lui `MOBILATE` din
 *  `canonical.ts`, care număra doar ce știa vechiul `ApartmentView.Furniture` —
 *  o listă mai scurtă decât ce se desenează acum. */
export const MOBILATE = new Set(["living", "bedroom", "dormitor", "kitchen",
  "bath", "bath2", "wc", "shower", "hall", "hol", "sas", "store", "ghena",
  "balcony", "scara", "lift"]);

/** Mobilierul unei camere, așezat în cadrul ei.
 *
 *  `pointerEvents="none"` pe tot grupul: mobilierul NU e obiect al
 *  documentului, deci un click pe canapea trebuie să selecteze camera de sub
 *  ea, nu să cadă în gol. Fără asta, exact suprafețele cele mai mari ale unei
 *  camere ar fi devenit moarte la apăsare.
 */
/** MEMOIZAT, și e o chestiune de viteză, nu de stil.
 *
 *  Fiecare piesă își are decupajul (`clipPath`) și FILTRUL ei de umbră. Un
 *  nivel are ordinul zecilor — 66 pe planul de referință — deci re-desenarea lor
 *  înseamnă tot atâtea rasterizări de filtru, nu una. `Mobilier` nu primește
 *  nimic legat de selecție (numai geometrie), dar trăiește într-un `StoreyView`
 *  care se re-randează la fiecare click; fără `memo`, un click pe o cameră
 *  refăcea umbra fiecărei piese de mobilier de pe etaj.
 *
 *  `memo` ține fiindcă toate proprietățile se compară superficial: `poly` e o
 *  referință din planul memoizat, `clipId` e un șir, restul sunt primitive.
 */
function MobilierBrut({ poly, kind, deg, pxPerM, clipId, plan }: {
  poly: number[][];
  kind?: string | null;
  deg?: number | null;
  pxPerM: number;
  /** Id-ul decupajului. Vezi mai jos de ce mobilierul se decupează. */
  clipId: string;
  /** Desenul de PLANȘĂ: piesele vin din `arhMobilierPlan`, nu de aici.
   *
   *  Două biblioteci, fiindcă cele două desene spun lucruri diferite — v. nota
   *  de la începutul lui `arhMobilierPlan`. Ce rămâne comun e tot ce ține de
   *  AȘEZARE: cadrul camerei, rotirea, decupajul, pragul de mărire. Alea sunt
   *  ale mobilării, nu ale stilului, și n-aveau de ce să fie scrise de două ori. */
  plan?: boolean;
  pick?: PickAttrs;
}) {
  if (!kind) return null;
  const areaPlan = plan && MOBILATE_PLAN.has(kind);
  if (!areaPlan && !MOBILATE.has(kind)) return null;
  const c = cadru(poly, deg);
  if (!c) return null;
  if (Math.min(c.w, c.h) * pxPerM < PRAG_MOBILIER) return null;
  const p = areaPlan ? piesaPlan(kind, c.w, c.h) : piesa(kind, c.w, c.h);
  if (!p) return null;
  return (
    <>
      {/* DECUPAJ PE CAMERĂ, nu doar reguli de încăpere.
          `cadru()` măsoară dreptunghiul în care intră inelul, iar o cameră în L
          — un living care înconjoară nucleul, cazul normal pe banda asta — are
          un dreptunghi mai mare decât ea. Pe el, o masă așezată „la 1,3 m de
          marginea din dreapta" cade în holul de alături: exact asta s-a și
          întâmplat. Regulile de încăpere rămân, fiindcă ele decid CE piesă se
          desenează; decupajul e plasa de siguranță care garantează că nimic nu
          se varsă în camera vecină, oricât de ciudat ar fi inelul. */}
      <clipPath id={clipId}>
        <polygon points={poly.map((q) => `${q[0]},${q[1]}`).join(" ")} />
      </clipPath>
      {/* Umbra piesei e a planului RANDAT. Pe desenul de linie n-are ce
          căuta — o umbră e un efect de lumină, iar planșa tehnică n-are lumină
          — și oricum `filter` nu supraviețuiește exportului PDF. Se stinge
          odată cu tonurile, prin aceeași variabilă: fără fond, fără umbră. */}
      <g clipPath={`url(#${clipId})`} pointerEvents="none"
         filter="var(--mob-umbra, url(#arh-umbra-mobila))">
        <g transform={`translate(${c.cx} ${c.cy}) rotate(${c.deg})`}>{p}</g>
      </g>
    </>
  );
}

export const Mobilier = memo(MobilierBrut);
